import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import { ANKH_SELLER } from "./ankhCustomers";
import { isMobile10 } from "../domain/testMobile";

/**
 * Platform (owner 2026-10-24, radius-wiki ankhos-platform-owner-model.md §13):
 * io.ankhos as the platform owner -- its tenants and their commercial state,
 * invitations, promotions. radaccess feat/platform-tenants
 * (handler/platformtenants.go), superadmin only, io.ankhos only.
 *
 *   GET  /billing/io.ankhos/tenants[?environment=live|test]
 *   GET  /billing/io.ankhos/tenants/{acct}             tenant + state history
 *   PUT  /billing/io.ankhos/tenants/{acct}/state       { state, reason }
 *   GET  /billing/io.ankhos/promotions
 *   POST /billing/io.ankhos/promotions
 *   PUT  /billing/io.ankhos/promotions/{code}/end
 *   GET  /billing/io.ankhos/invites
 *   POST /billing/io.ankhos/invites                    mobile-bound, 7 days; no SMS until DLT approves
 *   PUT  /billing/io.ankhos/invites/{id}/revoke
 *   PUT  /billing/io.ankhos/invites/{id}/resend        SMS again, once per 5 min (429 + retryafter)
 *   GET  /billing/io.ankhos/purge?namespace=           what deleting a TEST tenant removes (radaccess v1.148)
 *   POST /billing/io.ankhos/purge                      { namespace, confirm } -- deletes it for good
 *
 * 🔑 Which state may follow which is the gateway's answer (`nextstates` on
 * each tenant); the console only offers what it is given.
 */

const enc = encodeURIComponent;
const base = `/billing/${ANKH_SELLER}`;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const CODE = /^[A-Z0-9]{3,32}$/;

function uuid(id: string, what: string): string {
  if (!UUID.test(id)) throw new Error(`Not ${what}: ${id}`);
  return id;
}

export interface PlatformTenant {
  accountid: string;
  kind: string;
  environment: "live" | "test";
  namespace?: string;
  legalname: string;
  state: string;
  statesince: string;
  statereason?: string;
  source: string;
  promotioncode?: string;
  trialendsat?: string;
  createdby: string;
  createdat: string;
  nextstates: string[];
}

export interface TenantEvent {
  fromstate?: string;
  tostate: string;
  reason?: string;
  actor: string;
  at: string;
}

export interface PlatformPromotion {
  code: string;
  name: string;
  benefit: "free_days" | "percent_off" | "amount_off";
  value: number;
  months?: number;
  kinds: string[];
  validfrom: string;
  validto?: string;
  maxuses?: number;
  usedcount: number;
  isstandard: boolean;
  status: "ACTIVE" | "ENDED";
  createdby: string;
}

export interface PlatformInvite {
  id: string;
  kind: string;
  name: string;
  mobile: string;
  email?: string;
  promotioncode?: string;
  expiresat: string;
  status: string;
  sentby: string;
  createdat: string;
  /** pending · sending · sent · opted_out · failed · none (radaccess v1.144+). */
  smsstatus?: string;
  smserror?: string;
  /** Seconds before the SMS may be resent (0 = now); absent when it cannot be (radaccess v1.145+). */
  resendin?: number;
}

/** Plain words for the invitation SMS. */
export const SMS_LABEL: Record<string, string> = {
  pending: "Waiting (10:00–21:00)",
  sending: "Sending",
  sent: "Sent",
  opted_out: "Not sent: opted out",
  failed: "Failed",
  none: "Not texted",
};

export async function listTenants(
  identity: CallerIdentity,
  environment: "" | "live" | "test" = "",
  signal?: AbortSignal,
): Promise<{ tenants: PlatformTenant[]; kinds: string[] }> {
  const q = environment ? `?environment=${environment}` : "";
  const r = await callGateway<{ tenants: PlatformTenant[] | null; kinds: string[] | null }>(
    identity,
    config().gatewayBase,
    `${base}/tenants${q}`,
    signal ? { signal } : {},
  );
  return { tenants: r.data?.tenants ?? [], kinds: r.data?.kinds ?? [] };
}

export async function getTenant(identity: CallerIdentity, accountid: string): Promise<{ tenant: PlatformTenant; events: TenantEvent[] }> {
  const r = await callGateway<{ tenant: PlatformTenant; events: TenantEvent[] | null }>(
    identity,
    config().gatewayBase,
    `${base}/tenants/${uuid(accountid, "an account id")}`,
  );
  return { tenant: r.data.tenant, events: r.data.events ?? [] };
}

export async function setTenantState(identity: CallerIdentity, accountid: string, state: string, reason: string): Promise<PlatformTenant> {
  const r = await callGateway<PlatformTenant>(identity, config().gatewayBase, `${base}/tenants/${uuid(accountid, "an account id")}/state`, {
    method: "PUT",
    body: { state, reason: reason.trim() },
  });
  return r.data;
}

export async function listPromotions(identity: CallerIdentity, signal?: AbortSignal): Promise<{ promotions: PlatformPromotion[]; kinds: string[] }> {
  const r = await callGateway<{ promotions: PlatformPromotion[] | null; kinds: string[] | null }>(
    identity,
    config().gatewayBase,
    `${base}/promotions`,
    signal ? { signal } : {},
  );
  return { promotions: r.data?.promotions ?? [], kinds: r.data?.kinds ?? [] };
}

export interface PromotionInput {
  code: string;
  name: string;
  benefit: PlatformPromotion["benefit"];
  value: number;
  months?: number | undefined;
  kinds: string[];
  validfrom?: string | undefined;
  validto?: string | undefined;
  maxuses?: number | undefined;
  isstandard: boolean;
}

export async function createPromotion(identity: CallerIdentity, input: PromotionInput): Promise<PlatformPromotion> {
  const code = input.code.trim().toUpperCase();
  if (!CODE.test(code)) throw new Error("A code is 3 to 32 letters or digits");
  const r = await callGateway<PlatformPromotion>(identity, config().gatewayBase, `${base}/promotions`, {
    method: "POST",
    body: { ...input, code },
  });
  return r.data;
}

export async function endPromotion(identity: CallerIdentity, code: string): Promise<void> {
  if (!CODE.test(code)) throw new Error(`Not a promotion code: ${code}`);
  await callGateway(identity, config().gatewayBase, `${base}/promotions/${enc(code)}/end`, { method: "PUT", body: {} });
}

export async function listInvites(identity: CallerIdentity, signal?: AbortSignal): Promise<{ invites: PlatformInvite[]; kinds: string[]; smsready: boolean }> {
  const r = await callGateway<{ invites: PlatformInvite[] | null; kinds: string[] | null; smsready?: boolean }>(
    identity,
    config().gatewayBase,
    `${base}/invites`,
    signal ? { signal } : {},
  );
  return { invites: r.data?.invites ?? [], kinds: r.data?.kinds ?? [], smsready: Boolean(r.data?.smsready) };
}

export interface InviteInput {
  kind: string;
  name: string;
  mobile: string;
  email: string;
  promotioncode: string;
}

export async function createInvite(identity: CallerIdentity, input: InviteInput): Promise<{ invite: PlatformInvite; sms?: string }> {
  const r = await callGateway<{ invite: PlatformInvite; sms?: string }>(identity, config().gatewayBase, `${base}/invites`, {
    method: "POST",
    body: { ...input, promotioncode: input.promotioncode.trim().toUpperCase() },
  });
  return r.data;
}

/**
 * Assisted onboarding (owner 2026-10-24, radaccess v1.158+): platform staff
 * prepare the tenant; its owner proves their own mobile at /join.
 */
export interface AssistedInput {
  kind: "operator" | "cloud_tenant";
  operatortype: string;
  namespace: string;
  legalname: string;
  firstname: string;
  lastname: string;
  mobile: string;
  email: string;
  environment: "test" | "live";
  promotioncode: string;
}

export interface AssistedResult {
  accountid: string;
  namespace: string;
  state: string;
  inviteid: string;
  sms: string;
  next: string;
}

/** The operator types each kind may take (radaccess assistedTypes). */
export const ASSISTED_TYPES: Record<AssistedInput["kind"], readonly { code: string; label: string }[]> = {
  operator: [
    { code: "ISP", label: "Internet service provider (licensed)" },
    { code: "VNO", label: "Virtual network operator" },
    { code: "NETWORK_OPERATOR", label: "Network operator (campus, no internet resale)" },
    { code: "OPERATOR", label: "Operator (other)" },
  ],
  cloud_tenant: [{ code: "CLOUD_TENANT", label: "Cloud tenant" }],
};

/** A full namespace: three or more dot-separated labels, lower case. */
export function assistedNamespaceOK(ns: string): boolean {
  const v = ns.trim().toLowerCase();
  return v !== "io.ankhos" && /^[a-z0-9-]+(\.[a-z0-9-]+){2,}$/.test(v);
}

export async function createAssisted(identity: CallerIdentity, input: AssistedInput): Promise<AssistedResult> {
  const r = await callGateway<AssistedResult>(identity, config().gatewayBase, `${base}/assisted`, {
    method: "POST",
    body: {
      ...input,
      namespace: input.namespace.trim().toLowerCase(),
      promotioncode: input.promotioncode.trim().toUpperCase(),
    },
  });
  return r.data;
}

export async function revokeInvite(identity: CallerIdentity, id: string): Promise<void> {
  await callGateway(identity, config().gatewayBase, `${base}/invites/${uuid(id, "an invitation id")}/revoke`, { method: "PUT", body: {} });
}

/** Queue the invitation SMS again. Too soon is a 429 the caller shows as an error. */
export async function resendInvite(identity: CallerIdentity, id: string): Promise<{ sms: string }> {
  const r = await callGateway<{ sms: string }>(identity, config().gatewayBase, `${base}/invites/${uuid(id, "an invitation id")}/resend`, {
    method: "PUT",
    body: {},
  });
  return r.data;
}

export interface PurgeCount {
  table: string;
  column: string;
  rows: number;
}

export interface PurgePreview {
  namespace: string;
  allowed: boolean;
  reason?: string;
  istenant?: boolean;
  tenant?: { accountid: string; namespace: string; legalname: string };
  tables?: PurgeCount[] | null;
  rows?: number;
  namespaces?: string[] | null;
  logins?: { username: string; namespace: string; machine: boolean }[] | null;
  loginscheckable?: boolean;
  loginerror?: string;
}

export interface PurgeResult {
  namespace: string;
  rows: number;
  tables: PurgeCount[] | null;
  loginsdeleted: string[] | null;
  loginsfailed: string[] | null;
  loginnote?: string;
}

const NS = /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)+$/;

/** What deleting a test tenant (or a namespace inside one) would remove. */
export async function previewPurge(identity: CallerIdentity, namespace: string): Promise<PurgePreview> {
  const ns = namespace.trim();
  if (!NS.test(ns)) throw new Error("A full operator namespace, such as net.ankhnet.example");
  const r = await callGateway<PurgePreview>(identity, config().gatewayBase, `${base}/purge?namespace=${enc(ns)}`);
  return r.data;
}

/** Delete it for good. `confirm` must repeat the namespace. */
export async function purgeNamespace(identity: CallerIdentity, namespace: string, confirm: string): Promise<PurgeResult> {
  const r = await callGateway<PurgeResult>(identity, config().gatewayBase, `${base}/purge`, {
    method: "POST",
    body: { namespace: namespace.trim(), confirm: confirm.trim() },
  });
  return r.data;
}

/** A 10-digit Indian mobile, as the gateway will accept it (it normalises to +91). */
export function isIndianMobile(s: string): boolean {
  let d = s.replace(/[\s-]/g, "").replace(/^\+/, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  return isMobile10(d);
}

/** Plain words for a state and a kind. */
export const STATE_LABEL: Record<string, string> = {
  INVITED: "Invited",
  APPLIED: "Applied",
  IN_REVIEW: "In review",
  REJECTED: "Rejected",
  TRIAL: "Trial",
  ACTIVE: "Active",
  OVERDUE: "Overdue",
  RESTRICTED: "Restricted",
  SUSPENDED: "Suspended",
  CLOSING: "Closing",
  CLOSED: "Closed",
};

export const KIND_LABEL: Record<string, string> = {
  operator: "Operator",
  cloud_tenant: "Cloud tenant",
  billing_only: "Billing-only company",
};

export function promotionBenefit(p: Pick<PlatformPromotion, "benefit" | "value" | "months">): string {
  if (p.benefit === "free_days") return `${p.value} days free`;
  const n = p.months ? ` for ${p.months} invoice${p.months === 1 ? "" : "s"}` : "";
  return p.benefit === "percent_off" ? `${p.value}% off${n}` : `₹${p.value} off${n}`;
}
